/**
 * /pdf-templates — Plantillas PDF (Representación Gráfica DIAN).
 *
 * - Selector de tax_client (top).
 * - Tabla con las 3 doc_type (FE / NC / ND) mostrando si tienen
 *   custom o usan default, version y last update.
 * - Acciones por fila: Editar (link al editor) / Restaurar default (DELETE).
 *
 * No carga el editor Monaco aquí — el editor es una page separada
 * lazy-loaded. Esto mantiene esta página rápida.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FileType, Info, Loader2, Pencil, RotateCcw } from 'lucide-react'

import { ClientSelector } from './components/client-selector'
import { useTaxClients } from '@/hooks/api/use-tax-clients'
import {
  useDeletePdfTemplate,
  usePdfTemplates,
} from '@/hooks/api/use-pdf-templates'
import {
  DOC_TYPES,
  DOC_TYPE_LABELS,
  type DocType,
  type PdfTemplate,
} from '@/lib/api-pdf-templates'

export function PdfTemplatesPage() {
  const [clientId, setClientId] = useState<string>('')

  const clients = useTaxClients()
  const templates = usePdfTemplates(clientId || undefined)
  const del = useDeletePdfTemplate()

  /** Indexa los templates custom por doc_type para lookup O(1). */
  const customByDocType: Partial<Record<DocType, PdfTemplate>> = {}
  for (const t of templates.data?.items ?? []) {
    customByDocType[t.doc_type] = t
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">
            ▸ Personalización
          </p>
          <h1 className="mt-1 text-3xl font-bold text-fg-primary">Plantillas PDF</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg-secondary">
            Representación Gráfica DIAN (factura electrónica, nota crédito, nota
            débito). Si un tax-client no tiene plantilla custom, se usa el
            default de Taxor. Los templates personalizados siempre deben respetar
            los campos obligatorios del Anexo Técnico §11.4.
          </p>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-border bg-bg-surface p-4 shadow-sm">
        <ClientSelector
          value={clientId}
          onChange={setClientId}
          clients={clients.data?.items ?? []}
          loading={clients.isLoading}
        />
        {clients.isLoading && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-fg-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Cargando tax-clients…
          </p>
        )}
        {clients.isError && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            No se pudo cargar la lista de tax-clients. Recarga la página o
            inténtalo más tarde.
          </p>
        )}
      </div>

      {!clientId && (
        <div className="rounded-xl border border-border bg-bg-surface p-12 text-center shadow-sm">
          <FileType className="mx-auto h-10 w-10 text-fg-muted" />
          <p className="mt-4 text-sm text-fg-secondary">
            Seleccione un tax-client para gestionar sus plantillas PDF.
          </p>
        </div>
      )}

      {clientId && templates.isSuccess && templates.data.items.length === 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-4 py-3 text-sm text-fg-secondary">
          <Info className="h-4 w-4 flex-none text-fg-muted" />
          Todos los tipos usan la plantilla por defecto de Taxor. Edita
          cualquiera para crear una versión custom.
        </div>
      )}

      {clientId && templates.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          No se pudieron cargar las plantillas de este tax-client. Recarga la
          página o inténtalo más tarde.
        </div>
      )}

      {clientId && (
        <div className="overflow-hidden rounded-xl border border-border bg-bg-surface shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Tipo de documento
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Origen
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Versión
                </th>
                <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                  Última actualización
                </th>
                <th className="px-4 py-3 text-right font-semibold text-fg-secondary">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {DOC_TYPES.map((dt) => {
                const tpl = customByDocType[dt]
                const isCustom = !!tpl
                return (
                  <tr key={dt} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-fg-primary">
                      {DOC_TYPE_LABELS[dt]}
                    </td>
                    <td className="px-4 py-3">
                      {isCustom ? (
                        <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                          Custom
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-bg-elevated px-2 py-0.5 text-xs font-medium text-fg-muted">
                          Default Taxor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-fg-secondary">
                      {isCustom ? `v${tpl.version}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-fg-secondary">
                      {isCustom && tpl.updated_at
                        ? new Date(tpl.updated_at).toLocaleString('es-CO')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/pdf-templates/${clientId}/${dt}`}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary"
                        >
                          <Pencil className="h-3 w-3" />
                          Editar
                        </Link>
                        {isCustom && (
                          <button
                            type="button"
                            disabled={del.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  `¿Eliminar la plantilla custom de "${DOC_TYPE_LABELS[dt]}"? Volverá al default Taxor.`,
                                )
                              ) {
                                del.mutate({ clientId, docType: dt })
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Restaurar default
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {clientId && templates.isLoading && (
        <p className="mt-4 text-center text-xs text-fg-muted">Cargando plantillas…</p>
      )}
    </div>
  )
}
